import React, { useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Button, Input, Form, message, Card } from "antd";
import Header from "../../reusables/Header";
import Footer from "../../reusables/Footer";
import { addUserDirectUrewards, findCustomerByContact, provisionLoyaltyId } from "../../utilities/customerLoyaltyApi";
import LogoBlack from "../../assets/icons/logo_black.svg";

const JoinProgram = () => {
  const { merchantId } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const programId = query.get("program_id") || "15";

  const [loading, setLoading] = useState(false);
  const [enrolledUser, setEnrolledUser] = useState(null);
  const [form] = Form.useForm();

  const cardKitBaseUrl = process.env.REACT_APP_CARD_KIT_URL || "https://cardkit.loystar.co";

  const handleSubmit = async (values) => {
    const { firstName, phoneNumber, email } = values;
    setLoading(true);
    try {
      const response = await addUserDirectUrewards(
        merchantId,
        firstName,
        email || "",
        phoneNumber
      );

      const customerData = response?.data || response?.user || response;
      let loyaltyCode = (customerData?.loyalty_id && String(customerData.loyalty_id).length >= 8 && !String(customerData.loyalty_id).startsWith('LOY-'))
        ? String(customerData.loyalty_id)
        : null;

      // If loyalty code is not returned directly, fetch it from customer lookup
      if (!loyaltyCode && (phoneNumber || email) && merchantId) {
        try {
          const lookup = await findCustomerByContact(merchantId, phoneNumber || email);
          if (lookup?.loyalty_id && String(lookup.loyalty_id).length >= 8) {
            loyaltyCode = String(lookup.loyalty_id);
          }
        } catch (e) {
          console.error("Loyalty lookup error:", e);
        }
      }

      // If still missing, provision a fresh 13-digit loyalty code from the merchant's pool
      const internalId = customerData?.id || customerData?.customer_id || customerData?.user_id;
      if (!loyaltyCode && internalId && merchantId) {
        try {
          const provisioned = await provisionLoyaltyId(merchantId, internalId);
          if (provisioned && String(provisioned).length >= 8) {
            loyaltyCode = String(provisioned);
          }
        } catch (e) {
          console.error("Loyalty provision error:", e);
        }
      }

      const effectiveCustomerId = loyaltyCode || internalId || phoneNumber;
      
      const userData = {
        firstName,
        phoneNumber,
        customerId: effectiveCustomerId,
        loyaltyId: loyaltyCode || ""
      };

      setEnrolledUser(userData);
      message.success(`Welcome ${firstName}! Choose your wallet below.`);
    } catch (err) {
      console.error("Enrollment error:", err);
      setEnrolledUser({
        firstName,
        phoneNumber,
        customerId: phoneNumber,
        loyaltyId: ""
      });
      message.info(`Welcome ${firstName}! Choose your wallet below.`);
    } finally {
      setLoading(false);
    }
  };

  const passSerial = `LOY-${merchantId}-${programId}-${enrolledUser?.customerId || ''}`;
  const smartPassUrl = `${cardKitBaseUrl}/v1/passes/smart/${passSerial}?name=${encodeURIComponent(enrolledUser?.firstName || 'Valued Guest')}&customer_id=${encodeURIComponent(enrolledUser?.customerId || '')}&loyalty_id=${encodeURIComponent(enrolledUser?.loyaltyId || '')}&phone=${encodeURIComponent(enrolledUser?.phoneNumber || '')}&program_name=${encodeURIComponent('Loyalty Rewards Program')}&card_type=${encodeURIComponent('Points')}&reward_benefits=${encodeURIComponent('Earn rewards with every purchase')}&target_required=${encodeURIComponent('100 Points')}&current_balance=${encodeURIComponent('0 Pts')}`;

  return (
    <>
      <Header
        titleComponent={
          <h3 style={{ margin: 0 }}>
            <b>Loyalty Pass Enrollment</b>
          </h3>
        }
      />
      <div className="container_main" style={{ padding: "20px", maxWidth: "480px", margin: "0 auto", minHeight: "80vh" }}>
        <div style={{ textAlign: "center", marginBottom: "24px", marginTop: "12px" }}>
          <img src={LogoBlack} alt="Loystar" style={{ height: "44px" }} />
          <h2 style={{ fontSize: "1.5rem", marginTop: "12px", color: "#111827" }}>
            {enrolledUser ? "Save Your Loyalty Pass" : "Join Loyalty Program"}
          </h2>
          <p style={{ color: "#6B7280" }}>
            {enrolledUser
              ? "Tap below to add your pass to your phone's digital wallet."
              : "Enter your details below to receive your digital loyalty card."}
          </p>
        </div>

        <Card style={{ borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", border: "1px solid #E5E7EB" }}>
          {!enrolledUser ? (
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[{ required: true, message: "Please enter your first name" }]}
              >
                <Input placeholder="e.g. John" size="large" />
              </Form.Item>

              <Form.Item
                label="Phone Number"
                name="phoneNumber"
                rules={[{ required: true, message: "Please enter your phone number" }]}
              >
                <Input placeholder="e.g. 08012345678" size="large" />
              </Form.Item>

              <Form.Item label="Email (Optional)" name="email">
                <Input placeholder="e.g. john@example.com" size="large" type="email" />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                style={{
                  backgroundColor: "#68C222",
                  borderColor: "#68C222",
                  height: "48px",
                  borderRadius: "8px",
                  fontWeight: "600",
                  marginTop: "12px"
                }}
              >
                Register & Save Pass
              </Button>
            </Form>
          ) : (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <a
                href={smartPassUrl}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "14px 20px",
                  backgroundColor: "#000000",
                  color: "#FFFFFF",
                  borderRadius: "12px",
                  fontWeight: "600",
                  textDecoration: "none",
                  marginBottom: "16px",
                  fontSize: "1rem"
                }}
              >
                 Add to Apple Wallet
              </a>

              <a
                href={smartPassUrl}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "14px 20px",
                  backgroundColor: "#4285F4",
                  color: "#FFFFFF",
                  borderRadius: "12px",
                  fontWeight: "600",
                  textDecoration: "none",
                  fontSize: "1rem"
                }}
              >
                Save to Google Wallet
              </a>
            </div>
          )}
        </Card>
      </div>
      <Footer active="home" />
    </>
  );
};

export default JoinProgram;
